import React, { useEffect, useState } from 'react'

import track from 'react-tracking'
import OrderTrackingInfo from './OrderTrackingInfo'
import useOrderTracking from '../../../hooks/useOrderTracking'
import { Return, OrderItem } from '../../../utils/order-status/interfaces'
import Hero from '@ycos/component-hero'
import { CoremediaRenderer } from '@ycos/coremedia-renderer'

type OrderTrackingProps = {
    fetchOrderDetails: (externalOrderId: string, flag: boolean) => any
    fetchContent: (
        componentKey: string,
        countryCode?: string | null,
        options?: { mv?: 'gitMarkdown'; f?: 'ycos' } | null,
        v2?: boolean
    ) => any
    orderDateRaw: string
    externalOrderId: string
    orderStatus: string
    activeOrderStatus: string
    courierTrackingId: string
    timeDeliveredRaw: string
    dateShippedRaw: string
    messages: any
    shippingAddress: {
        country: string
    }
    tracking: {
        getTrackingData: () => void
        trackEvent: (event: any) => void
    }
    trackingUrl?: string
    returnTrackingUrl?: string
    shippingMethod: Array<string>
    locale: {
        country: string
        currency: string
        id: string
        langISO: string
        language: string
    }
    guestUser: boolean
    returns: Array<Return>
    orderItems: Array<OrderItem>
    isPartialReturnRefused: boolean
    isOrderDetailsFetched: boolean
    location: any
    shipmentsWithItemDetails: any
}

const OrderTracking: React.FC<OrderTrackingProps> = (props) => {
    const {
        fetchOrderDetails,
        externalOrderId,
        courierTrackingId,
        timeDeliveredRaw,
        dateShippedRaw,
        messages,
        shippingAddress,
        shippingMethod,
        activeOrderStatus,
        trackingUrl = '',
        returnTrackingUrl = '',
        returns,
        orderItems,
        isPartialReturnRefused,
        guestUser,
        isOrderDetailsFetched,
        fetchContent,
        shipmentsWithItemDetails,
    } = props

    const isReturn = returns?.length > 0
    const trackingNumber = isReturn ? orderItems?.[0]?.extnReturnTrackingNo : courierTrackingId
    const [contentData, setContentData] = useState()

    const splitShipment = shipmentsWithItemDetails?.length > 1

    const splitShipmentTrackingUrl =
        splitShipment && props.location.query.trackingUrl !== 'undefined'
            ? decodeURIComponent(props.location.query.trackingUrl)
            : trackingUrl

    const splitShipmentTrackingNumber =
        splitShipment && props.location.query.courierTrackingId !== 'undefined'
            ? props.location.query.courierTrackingId
            : trackingNumber

    const splitShipmentReturnTrackingUrl = splitShipment
        ? decodeURIComponent(props.location.query.returnTrackingUrl).replace(/^undefined$/, '')
        : returnTrackingUrl

    let trackingData

    if (splitShipment) {
        const shipment =
            shipmentsWithItemDetails.find(
                (shipment) => splitShipmentTrackingNumber && splitShipmentTrackingNumber === shipment.courierTrackingId
            ) ||
            shipmentsWithItemDetails.find(
                (shipment) =>
                    splitShipmentReturnTrackingUrl === shipment.returnTrackingUrl ||
                    splitShipmentTrackingUrl === shipment.trackingUrl
            )

        trackingData = useOrderTracking({
            orderStatus: shipment?.shipmentItemDetails[0]?.activeOrderItemStatus,
            orderId: shipment?.isReturnOrExchangeShipment ? returns?.[0]?.returnIdentifier : externalOrderId,
            trackingNumber: splitShipmentTrackingNumber,
            orderInformation: {
                timeDelivered: shipment?.timeDeliveredRaw,
                dateShipped: shipment?.dateShippedRaw,
                shippingCountry: shippingAddress?.country,
                shippingMethod,
                isReturn: !!shipment?.isReturnOrExchangeShipment,
                isPartialReturnRefused,
            },
        })
    } else {
        trackingData = useOrderTracking({
            orderStatus: activeOrderStatus,
            orderId: isReturn ? returns?.[0]?.returnIdentifier : externalOrderId,
            trackingNumber,
            orderInformation: {
                timeDelivered: timeDeliveredRaw,
                dateShipped: dateShippedRaw,
                shippingCountry: shippingAddress?.country,
                shippingMethod,
                isReturn,
                isPartialReturnRefused,
            },
        })
    }

    useEffect(() => {
        !guestUser || !isOrderDetailsFetched ? fetchOrderDetails(externalOrderId, false) : null

        const fetchContentComponent = async () => {
            const { payload } = (await fetchContent('order-tracking')) || {}
            payload && setContentData(payload)
        }
        fetchContentComponent()
    }, [externalOrderId])

    return (
        <>
            <OrderTrackingInfo
                title={isReturn ? messages.returnTracking.trackDetails?.() : messages.orderTracking.trackDetails?.()}
                subTitle={isReturn ? messages.returnTracking.subTitle?.() : messages.orderTracking.subTitle?.()}
                {...props}
                trackingData={trackingData}
                courierTrackingUrl={isReturn ? splitShipmentReturnTrackingUrl : splitShipmentTrackingUrl}
                courierTrackingMessage={
                    isReturn
                        ? messages.returnTracking.courierTrackingNumber({ trackingNumber: splitShipmentTrackingNumber })
                        : messages.orderTracking.courierTrackingNumber({ trackingNumber: splitShipmentTrackingNumber })
                }
                type={isReturn ? 'return' : 'order'}
            />
            {contentData && CoremediaRenderer(contentData, [Hero])}
        </>
    )
}

export default track()(OrderTracking)
